import React, { useState, useEffect } from 'react';
import { Service, User } from '../../types';
import { apiService } from '../../services/api';
import { CheckCircle, Info, Users, Heart } from 'lucide-react';

interface ThemeStyles {
  pageBg: string;
  cardBg: string;
  textPrimary: string;
  textSecondary: string;
  border: string;
  buttonPrimary: string;
  buttonSecondary: string;
  buttonSuccess: string;
  progressActive: string;
  progressInactive: string;
  selected: string;
  unselected: string;
  success: string;
  error: string;
  inlineStyles: {
    pageBg: React.CSSProperties;
    cardBg: React.CSSProperties;
    textPrimary: React.CSSProperties;
    textSecondary: React.CSSProperties;
    border: React.CSSProperties;
    buttonPrimary: React.CSSProperties;
    buttonPrimaryHover: React.CSSProperties;
    buttonSecondary: React.CSSProperties;
    buttonSecondaryHover: React.CSSProperties;
    buttonSuccess: React.CSSProperties;
    progressActive: React.CSSProperties;
    progressInactive: React.CSSProperties;
    selected: React.CSSProperties;
    unselected: React.CSSProperties;
    success: React.CSSProperties;
    error: React.CSSProperties;
  };
}

interface BookingStep2Props {
  selectedServices: Service[];
  professionals: User[];
  selectedProfessionals: { [serviceId: string]: User | undefined };
  onProfessionalsChange: (professionals: { [serviceId: string]: User | undefined }) => void;
  professionalCapabilities: { [professionalId: string]: any[] };
  loadingCapabilities: boolean;
  sameProfessionalForAll: boolean;
  onSameProfessionalForAllChange: (same: boolean) => void;
  themeStyles?: ThemeStyles;
  businessId: string;
  insistOnProfessional?: { [serviceId: string]: boolean };
  onInsistOnProfessionalChange?: (insistOnProfessional: { [serviceId: string]: boolean }) => void;
}

const BookingStep2: React.FC<BookingStep2Props> = ({
  selectedServices,
  professionals,
  selectedProfessionals,
  onProfessionalsChange,
  professionalCapabilities,
  loadingCapabilities,
  sameProfessionalForAll,
  onSameProfessionalForAllChange,
  themeStyles,
  businessId,
  insistOnProfessional = {},
  onInsistOnProfessionalChange
}) => {
  const [professionalSchedules, setProfessionalSchedules] = useState<{[professionalId: string]: any}>({});
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [priorityByProfessionalId, setPriorityByProfessionalId] = useState<Record<string, number>>({});

  // Get primary color from theme or default
  const primaryColor = themeStyles?.inlineStyles?.buttonPrimary.backgroundColor || '#3B82F6';

  // Fetch schedules for all professionals
  const fetchProfessionalSchedules = async () => {
    if (professionals.length === 0 || !businessId) return;
    
    setLoadingSchedules(true);
    try {
      const schedulePromises = professionals.map(async (professional) => {
        try {
          const schedules = await apiService.getProfessionalAvailability(businessId, professional.id);
          return { professionalId: professional.id, schedules };
        } catch (error) {
          console.error(`Error fetching schedule for ${professional.id}:`, error);
          return { professionalId: professional.id, schedules: [] };
        }
      });
      
      const results = await Promise.all(schedulePromises);
      const schedulesMap: {[professionalId: string]: any} = {};
      
      results.forEach(result => {
        schedulesMap[result.professionalId] = result.schedules;
      });
      
      setProfessionalSchedules(schedulesMap);
    } catch (error) {
      console.error('Error fetching professional schedules:', error);
    } finally {
      setLoadingSchedules(false);
    }
  };

  // Fetch schedules when professionals change
  useEffect(() => {
    fetchProfessionalSchedules();
  }, [professionals.map(p => p.id).join(','), businessId]);

  // Fetch display priorities (display-only ordering for customer selection UI)
  useEffect(() => {
    const fetchPriorities = async () => {
      if (!businessId) return;
      try {
        const rows = await apiService.getProfessionalDisplayPriorities(businessId);
        const map: Record<string, number> = {};
        rows.forEach((r) => {
          if (r && typeof r.professionalId === 'string' && typeof r.priority === 'number') {
            map[r.professionalId] = r.priority;
          }
        });
        setPriorityByProfessionalId(map);
      } catch (error) {
        // Fail open: preserve existing ordering if priorities can't be loaded
        console.error('Error fetching professional display priorities:', error);
        setPriorityByProfessionalId({});
      }
    };

    // Only relevant when there could be multiple professionals to display
    if (professionals.length >= 2 && businessId) {
      fetchPriorities();
    } else {
      setPriorityByProfessionalId({});
    }
  }, [businessId, professionals.length]);

  const orderProfessionalsForDisplay = (list: User[]): User[] => {
    /**
     * IMPORTANT: Do NOT change eligibility/selection logic.
     * This function is display-only, stable, and preserves the existing order unless priorities exist.
     */
    if (list.length < 2) return list;
    if (Object.keys(priorityByProfessionalId).length === 0) return list;

    return list
      .map((p, idx) => ({
        p,
        idx,
        priority: priorityByProfessionalId[p.id]
      }))
      .sort((a, b) => {
        const aHas = typeof a.priority === 'number';
        const bHas = typeof b.priority === 'number';

        if (aHas && bHas && a.priority !== b.priority) return a.priority - b.priority;
        if (aHas !== bHas) return aHas ? -1 : 1;

        // Stable fallback: preserve existing order exactly
        return a.idx - b.idx;
      })
      .map(x => x.p);
  };

  const handleProfessionalSelect = (serviceId: string, professional: User | null) => {
    onProfessionalsChange({
      ...selectedProfessionals,
      [serviceId]: professional || undefined
    });
    // Clear insistOnProfessional when professional is deselected or changed
    if (!professional && onInsistOnProfessionalChange) {
      const updated = { ...insistOnProfessional };
      delete updated[serviceId];
      onInsistOnProfessionalChange(updated);
    } else if (professional && onInsistOnProfessionalChange) {
      // Reset insistOnProfessional when a different professional is selected
      const updated = { ...insistOnProfessional };
      updated[serviceId] = false;
      onInsistOnProfessionalChange(updated);
    }
  };

  const handleInsistToggle = (e: React.MouseEvent, serviceId: string) => {
    e.stopPropagation(); // Prevent card selection when clicking heart
    if (!onInsistOnProfessionalChange) return;
    
    const updated = { ...insistOnProfessional };
    updated[serviceId] = !updated[serviceId];
    onInsistOnProfessionalChange(updated);
  };

  // Check if a professional can perform all selected services
  const canPerformAllServices = (professionalId: string): boolean => {
    if (selectedServices.length === 0) return true;
    return selectedServices.every(service => {
      const capabilities = professionalCapabilities[professionalId] || [];
      return capabilities.some((capability: any) => {
        const capId = capability.id || capability.serviceId || capability.service_id;
        return String(capId) === String(service.id);
      });
    });
  };

  // Filter professionals who can perform a specific service
  const getProfessionalsForService = (serviceId: string) => {
    return professionals.filter(professional => {
      // First check if professional can perform services
      if (professional.canPerformServices === false) {
        return false;
      }
      
      // Then check service capabilities
      const capabilities = professionalCapabilities[professional.id] || [];
      const hasCapability = capabilities.some((capability: any) => {
        const capId = capability.id || capability.serviceId || capability.service_id;
        return String(capId) === String(serviceId);
      });
      
      if (!hasCapability) {
        return false;
      }
      
      // Only filter by schedule if schedules have been loaded
      // If still loading, show the professional (will be filtered once loading completes)
      if (!loadingSchedules) {
        // Check if professional has a work schedule
        const schedule = professionalSchedules[professional.id];
        if (!schedule || schedule.length === 0) {
          return false; // Don't display professionals without schedules
        }
      }
      
      return true;
    });
  };

  // Filter professionals who can perform all selected services
  const getProfessionalsForAllServices = () => {
    return professionals.filter(professional => {
      // First check if professional can perform services
      if (professional.canPerformServices === false) {
        return false;
      }
      
      // Check if professional can perform ALL selected services
      if (!canPerformAllServices(professional.id)) {
        return false;
      }
      
      // Only filter by schedule if schedules have been loaded
      if (!loadingSchedules) {
        // Check if professional has a work schedule
        const schedule = professionalSchedules[professional.id];
        if (!schedule || schedule.length === 0) {
          return false; // Don't display professionals without schedules
        }
      }
      
      return true;
    });
  };

  const getProfessionalInitials = (professional: User) => {
    const firstName = professional.firstName || '';
    const lastName = professional.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getProfessionalDisplayName = (professional: User) => {
    if (professional.firstName && professional.lastName) {
      return `${professional.firstName} ${professional.lastName.charAt(0)}.`;
    }
    return professional.email;
  };

  const formatScheduleDisplay = (schedules: any[]) => {
    if (!schedules || schedules.length === 0) {
      return 'No schedule available';
    }

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const scheduleByDay: {[day: string]: any[]} = {};
    
    // Group schedules by day
    schedules.forEach(schedule => {
      const dayName = dayNames[schedule.dayOfWeek];
      if (!scheduleByDay[dayName]) {
        scheduleByDay[dayName] = [];
      }
      scheduleByDay[dayName].push(schedule);
    });

    // Format the display
    const formattedDays = Object.keys(scheduleByDay).map(day => {
      const daySchedules = scheduleByDay[day];
      const timeRanges = daySchedules
        .filter(s => !s.isBreakTime)
        .map(s => `${s.startTime} - ${s.endTime}`)
        .join(', ');
      return `${day}: ${timeRanges || 'Unavailable'}`;
    });

    return formattedDays.join(' | ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <Users className="w-4 h-4" style={{ color: primaryColor }} />
          </div>
          <h2 className="text-xl text-gray-900">Select Professionals</h2>
        </div>
      </div>

      {/* Info Message */}
      <div 
        className="border rounded-lg p-4"
        style={{ 
          backgroundColor: `${primaryColor}10`,
          borderColor: `${primaryColor}30`
        }}
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <Info className="w-5 h-5" style={{ color: primaryColor }} />
            </div>
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-gray-900 mb-1">
              Professional Selection Tip
            </h4>
            <p className="text-sm text-gray-700 leading-relaxed">
              Selecting a preferred professional for your service is subject to availability and may limit your schedule flexibility.
            </p>
          </div>
        </div>
      </div>

      {/* Services with Professional Selection */}
      {sameProfessionalForAll ? (
        // Single selection for all services
        <>
          <label className="flex items-center gap-2 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={sameProfessionalForAll}
              onChange={(e) => {
                onSameProfessionalForAllChange(e.target.checked);
                // When unchecking, clear all professional selections
                if (!e.target.checked) {
                  const clearedProfessionals: { [serviceId: string]: User | undefined } = {};
                  selectedServices.forEach(service => {
                    clearedProfessionals[service.id] = undefined;
                  });
                  onProfessionalsChange(clearedProfessionals);
                } else {
                  // When checking, apply the same professional to all services if one is selected
                  const firstService = selectedServices[0];
                  const selectedProfessional = selectedProfessionals[firstService?.id];
                  if (selectedProfessional) {
                    const sameProfessionals: { [serviceId: string]: User | undefined } = {};
                    selectedServices.forEach(service => {
                      sameProfessionals[service.id] = selectedProfessional;
                    });
                    onProfessionalsChange(sameProfessionals);
                  }
                }
              }}
              className="w-4 h-4 border-gray-300 rounded"
              style={{
                accentColor: primaryColor,
                color: primaryColor
              }}
            />
            <span className="text-sm font-medium text-gray-700">Same Professional for all services</span>
          </label>
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Select a Professional</h3>
            <p className="text-gray-600 text-sm">Choose a professional who can perform all selected services</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Individual Professional Options - Only show those who can perform all services */}
            {loadingCapabilities ? (
              <div className="col-span-full flex items-center justify-center py-12">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="text-sm text-gray-600">Loading professional capabilities...</span>
                </div>
              </div>
            ) : (
              orderProfessionalsForDisplay(getProfessionalsForAllServices()).map((professional) => {
                const firstService = selectedServices[0];
                const isSelected = selectedProfessionals[firstService?.id]?.id === professional.id;
                const isInsisted = firstService && insistOnProfessional[firstService.id] === true;
                
                return (
                  <div
                    key={professional.id}
                    className={`relative bg-white border-2 rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                      isSelected 
                        ? 'shadow-sm' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={isSelected ? { borderColor: primaryColor } : {}}
                    onClick={() => {
                      const sameProfessionals: { [serviceId: string]: User | undefined } = {};
                      selectedServices.forEach(service => {
                        sameProfessionals[service.id] = professional;
                      });
                      onProfessionalsChange(sameProfessionals);
                      // Reset insistOnProfessional when professional changes
                      if (onInsistOnProfessionalChange) {
                        const updated = { ...insistOnProfessional };
                        selectedServices.forEach(service => {
                          updated[service.id] = false;
                        });
                        onInsistOnProfessionalChange(updated);
                      }
                    }}
                  >
                    {/* Checkbox at top-right corner */}
                    <div 
                      className="absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                      style={isSelected 
                        ? { borderColor: primaryColor, backgroundColor: primaryColor } 
                        : { borderColor: '#D1D5DB' }
                      }
                    >
                      {isSelected && (
                        <CheckCircle className="w-3 h-3 text-white" />
                      )}
                    </div>
                    {/* Heart icon for insist on professional - only show when selected */}
                    {isSelected && firstService && onInsistOnProfessionalChange && (
                      <button
                        onClick={(e) => {
                          const updated = { ...insistOnProfessional };
                          // Apply to all services when sameProfessionalForAll is true
                          selectedServices.forEach(service => {
                            updated[service.id] = !isInsisted;
                          });
                          onInsistOnProfessionalChange(updated);
                          e.stopPropagation();
                        }}
                        className={`absolute top-3 right-10 w-5 h-5 rounded-full flex items-center justify-center transition-all z-10 ${
                          isInsisted 
                            ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                            : 'text-gray-400 hover:text-red-500 bg-white hover:bg-gray-50'
                        }`}
                        aria-label={isInsisted ? 'Remove insist on professional' : 'Insist on this professional'}
                        title={isInsisted ? 'Remove insist on professional' : 'Insist on this professional'}
                      >
                        <Heart 
                          className={`w-3.5 h-3.5 ${isInsisted ? 'fill-current' : ''}`}
                        />
                      </button>
                    )}
                    <div className="flex items-start space-x-3">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                        style={isSelected 
                          ? { backgroundColor: primaryColor } 
                          : { backgroundColor: '#9CA3AF' }
                        }
                      >
                        {getProfessionalInitials(professional)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-base font-semibold text-gray-900 truncate">
                              {getProfessionalDisplayName(professional)}
                            </h4>
                            {/* Service Permission Indicator */}
                            {professional.canPerformServices !== false && (
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-xs text-gray-500">Service provider</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            {!loadingCapabilities && !loadingSchedules && getProfessionalsForAllServices().length === 0 && (
              <div className="col-span-full rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                No professionals are currently available for all selected services. You may continue and the business will assign someone based on availability.
              </div>
            )}
          </div>
        </div>
        </>
      ) : (
        // Per-service selection (existing UI)
        <>
          <label className="flex items-center gap-2 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={sameProfessionalForAll}
              onChange={(e) => {
                onSameProfessionalForAllChange(e.target.checked);
                // When unchecking, clear all professional selections
                if (!e.target.checked) {
                  const clearedProfessionals: { [serviceId: string]: User | undefined } = {};
                  selectedServices.forEach(service => {
                    clearedProfessionals[service.id] = undefined;
                  });
                  onProfessionalsChange(clearedProfessionals);
                } else {
                  // When checking, apply the same professional to all services if one is selected
                  const firstService = selectedServices[0];
                  const selectedProfessional = selectedProfessionals[firstService?.id];
                  if (selectedProfessional) {
                    const sameProfessionals: { [serviceId: string]: User | undefined } = {};
                    selectedServices.forEach(service => {
                      sameProfessionals[service.id] = selectedProfessional;
                    });
                    onProfessionalsChange(sameProfessionals);
                  }
                }
              }}
              className="w-4 h-4 border-gray-300 rounded"
              style={{
                accentColor: primaryColor,
                color: primaryColor
              }}
            />
            <span className="text-sm font-medium text-gray-700">Same Professional for all services</span>
          </label>
          <div className="space-y-8">
          {selectedServices.map((service) => (
          <div key={service.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            {/* Service Header */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.name}</h3>
              <p className="text-gray-600 text-sm">Select a professional for this service</p>
            </div>
            
            {/* Professional Options Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Individual Professional Options */}
              {loadingCapabilities ? (
                <div className="col-span-full flex items-center justify-center py-12">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="text-sm text-gray-600">Loading professional capabilities...</span>
                  </div>
                </div>
              ) : (
                orderProfessionalsForDisplay(getProfessionalsForService(service.id)).map((professional) => {
                  const isSelected = selectedProfessionals[service.id]?.id === professional.id;
                  const isInsisted = insistOnProfessional[service.id] === true;
                  
                  return (
                    <div
                      key={professional.id}
                      className={`relative bg-white border-2 rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                        isSelected 
                          ? 'shadow-sm' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={isSelected ? { borderColor: primaryColor } : {}}
                      onClick={() => handleProfessionalSelect(service.id, professional)}
                    >
                      {/* Checkbox at top-right corner */}
                      <div 
                        className="absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                        style={isSelected 
                          ? { borderColor: primaryColor, backgroundColor: primaryColor } 
                          : { borderColor: '#D1D5DB' }
                        }
                      >
                        {isSelected && (
                          <CheckCircle className="w-3 h-3 text-white" />
                        )}
                      </div>
                      {/* Heart icon for insist on professional - only show when selected */}
                      {isSelected && onInsistOnProfessionalChange && (
                        <button
                          onClick={(e) => handleInsistToggle(e, service.id)}
                          className={`absolute top-3 right-10 w-5 h-5 rounded-full flex items-center justify-center transition-all z-10 ${
                            isInsisted 
                              ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                              : 'text-gray-400 hover:text-red-500 bg-white hover:bg-gray-50'
                          }`}
                          aria-label={isInsisted ? 'Remove insist on professional' : 'Insist on this professional'}
                          title={isInsisted ? 'Remove insist on professional' : 'Insist on this professional'}
                        >
                          <Heart 
                            className={`w-3.5 h-3.5 ${isInsisted ? 'fill-current' : ''}`}
                          />
                        </button>
                      )}
                      <div className="flex items-start space-x-3">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                          style={isSelected 
                            ? { backgroundColor: primaryColor } 
                            : { backgroundColor: '#9CA3AF' }
                          }
                        >
                          {getProfessionalInitials(professional)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="mb-2">
                            <div className="flex-1 min-w-0">
                              <h4 className="text-base font-semibold text-gray-900 truncate">
                                {getProfessionalDisplayName(professional)}
                              </h4>
                              {/* Service Permission Indicator */}
                              {professional.canPerformServices !== false && (
                                <div className="flex items-center gap-1 mt-1">
                                  <span className="text-xs text-gray-500">Service provider</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              {!loadingCapabilities && !loadingSchedules && getProfessionalsForService(service.id).length === 0 && (
                <div className="col-span-full rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                  No professionals are currently available for this service. You may continue and the business will assign someone based on availability.
                </div>
              )}
            </div>
            
            {/* Selected Professional Confirmation */}
            {selectedProfessionals[service.id] && (
              <div 
                className="mt-4 p-4 border rounded-lg"
                style={{ 
                  backgroundColor: `${primaryColor}10`,
                  borderColor: `${primaryColor}30`
                }}
              >
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: primaryColor }} />
                  <span className="text-sm font-medium text-gray-900">
                    Selected: <span style={{ color: primaryColor }}>
                      {getProfessionalDisplayName(selectedProfessionals[service.id]!)}
                    </span>
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
        </div>
        </>
      )}

    </div>
  );
};

export default BookingStep2;
